import type {
  ButtonHTMLAttributes,
  CSSProperties,
  KeyboardEventHandler,
  PointerEventHandler,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import type { TeamExternalStream } from '@/utils/teamLiveExternalStreams';
import type { TeamLiveGridMode } from '@/utils/teamLiveGrid';
import type { TeamLiveDeviceEntry, TeamLiveStreamEntry } from '@/types';

/** One grid slot, already ordered and capped by the page. */
export type TeamLiveGridTile =
  | {
      kind: 'member';
      key: string;
      entry: TeamLiveStreamEntry;
      /** Accessible name for the drag handle (member or vehicle name). */
      handleLabel: string;
    }
  | {
      /** Online-but-idle device (no stream — presence-only tile). */
      kind: 'device';
      key: string;
      entry: TeamLiveDeviceEntry;
      handleLabel: string;
    }
  | {
      kind: 'external';
      key: string;
      stream: TeamExternalStream;
      handleLabel: string;
    };

/**
 * What a tile needs to become draggable. Pointer activation lives on the tile's
 * strap (a big target, and the only surface that works over an external
 * stream's iframe); keyboard activation and the accessible name live on the
 * small grip glyph inside it, which dnd-kit's KeyboardSensor focuses.
 */
export interface TeamLiveTileDrag {
  /** Spread onto the strap element. */
  strapProps: {
    onPointerDown?: PointerEventHandler<HTMLElement>;
    style: CSSProperties;
  };
  /** Spread onto the grip button inside the strap. */
  gripProps: ButtonHTMLAttributes<HTMLButtonElement>;
}

/** Optional extras the mobile focus viewer passes on top of the grid contract. */
export interface TeamLiveTileExtras {
  strapHidden?: boolean;
  onExpand?: () => void;
}

/** Fixed rows x cols per mode; mode 1 additionally fits its tile to a card (see SortableTile). */
const GRID_CLASSES: Record<TeamLiveGridMode, string> = {
  1: 'grid-cols-1 grid-rows-1',
  4: 'grid-cols-2 grid-rows-2',
  9: 'grid-cols-3 grid-rows-3',
};

export interface TeamLiveSortableGridProps {
  mode: TeamLiveGridMode;
  tiles: TeamLiveGridTile[];
  /** Persist the new full order (tile keys) after a completed drag. */
  onOrderChange: (orderedKeys: string[]) => void;
  /** The page supplies tile bodies so recording/remove handlers stay there.
   *  The grid itself only ever calls with (entry, drag); the third param
   *  exists so the page's renderer can double as the focus viewer's (which
   *  passes TeamLiveTileExtras on top of the grid contract). */
  renderMemberTile: (
    entry: TeamLiveStreamEntry,
    drag: TeamLiveTileDrag | null,
    extra?: TeamLiveTileExtras
  ) => ReactNode;
  renderDeviceTile: (
    entry: TeamLiveDeviceEntry,
    drag: TeamLiveTileDrag | null
  ) => ReactNode;
  renderExternalTile: (
    stream: TeamExternalStream,
    drag: TeamLiveTileDrag | null,
    extra?: TeamLiveTileExtras
  ) => ReactNode;
  /** Mobile stack: vertical scrolling list instead of a fixed viewport grid. */
  mobile?: boolean;
  /** Mobile columns: 1 portrait / 2 landscape (ignored when not mobile). */
  mobileColumns?: 1 | 2;
  /** Mobile edit mode: show per-tile move arrows; drag stays off. */
  editMode?: boolean;
  /** Edit-mode move request (up = -1, down = 1). */
  onMoveTile?: (key: string, direction: -1 | 1) => void;
}

function SortableTile({
  tile,
  canReorder,
  fitted,
  stack,
  editMode,
  onMoveTile,
  render,
}: {
  tile: TeamLiveGridTile;
  canReorder: boolean;
  /** Single-tile mode: center a landscape-shaped card instead of filling the slot. */
  fitted: boolean;
  /** Mobile stack slot: aspect-video cell, no drag surface. */
  stack?: boolean;
  /** Mobile edit mode: show move arrows (stack only). */
  editMode?: boolean;
  /** Edit-mode move request (up = -1, down = 1). */
  onMoveTile?: (key: string, direction: -1 | 1) => void;
  render: (drag: TeamLiveTileDrag | null) => ReactNode;
}) {
  const { t } = useTranslation('pages');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.key, disabled: !canReorder || stack });

  // dnd-kit types its synthetic listeners as bare `Function`, so the handlers
  // need narrowing before they can be spread onto real elements.
  const onPointerDown = listeners?.onPointerDown as
    | PointerEventHandler<HTMLElement>
    | undefined;
  const onKeyDown = listeners?.onKeyDown as
    | KeyboardEventHandler<HTMLButtonElement>
    | undefined;

  const drag: TeamLiveTileDrag | null =
    canReorder && !stack
      ? {
          strapProps: {
            onPointerDown,
            // Required by PointerSensor on touch: without it the browser claims
            // the gesture for scrolling before dnd-kit sees the move.
            style: {
              touchAction: 'none',
              cursor: isDragging ? 'grabbing' : 'grab',
            },
          },
          gripProps: {
            ...attributes,
            onKeyDown,
            'aria-label': t('teamLive.reorderHandle', {
              title: tile.handleLabel,
            }),
          },
        }
      : null;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.75 : 1,
        zIndex: isDragging ? 30 : undefined,
      }}
      className={clsx(
        stack
          ? 'relative aspect-video'
          : fitted
            ? 'relative flex min-h-0 items-center justify-center [container-type:size]'
            : 'relative grid min-h-0',
      )}
    >
      {!stack && fitted ? (
        <div
          data-testid="team-live-fitted-tile"
          // Landscape-shaped card (portrait streams pillarbox inside it like a
          // normal player): width-driven 16:9, height capped at the available
          // area via container-query units, 320px minimum floor.
          // docs/superpowers/specs/2026-09-01-team-live-portrait-tile-design.md
          className="grid aspect-video w-[min(100%,calc(100cqh*16/9))] min-h-[min(320px,100cqh)] grid-cols-1 grid-rows-1"
        >
          {render(drag)}
        </div>
      ) : (
        render(drag)
      )}
      {stack && editMode && onMoveTile ? (
        <div className="absolute right-1 top-1 z-10 flex flex-col gap-1">
          <button
            type="button"
            aria-label={t('teamLive.moveUp', { title: tile.handleLabel })}
            onClick={() => onMoveTile(tile.key, -1)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/25 bg-black/70 text-white/90 backdrop-blur-sm active:bg-black/90"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={t('teamLive.moveDown', { title: tile.handleLabel })}
            onClick={() => onMoveTile(tile.key, 1)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/25 bg-black/70 text-white/90 backdrop-blur-sm active:bg-black/90"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The Team Live multiview grid: fixed rows x cols per mode, tiles
 * drag-reorderable via dnd-kit (pointer or keyboard). The page computes the
 * ordered, capped tile list; this component only renders and reports
 * reorderings.
 *
 * The grid no longer draws a handle of its own — it hands each tile a
 * TeamLiveTileDrag so the tile's strap becomes the drag surface (issue #738;
 * the old floating handle overlapped the player's fullscreen button). See
 * docs/superpowers/specs/2026-08-24-team-live-tile-reorder-design.md
 */
export function TeamLiveSortableGrid({
  mode,
  tiles,
  onOrderChange,
  renderMemberTile,
  renderDeviceTile,
  renderExternalTile,
  mobile,
  mobileColumns,
  editMode,
  onMoveTile,
}: TeamLiveSortableGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = tiles.findIndex((tile) => tile.key === active.id);
    const newIndex = tiles.findIndex((tile) => tile.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onOrderChange(arrayMove(tiles, oldIndex, newIndex).map((tile) => tile.key));
  };

  const stackClasses =
    mobileColumns === 2 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={tiles.map((tile) => tile.key)}
        strategy={rectSortingStrategy}
      >
        <div
          data-testid="team-live-grid"
          className={clsx(
            'grid min-h-0 flex-1',
            mobile
              ? clsx(
                  'auto-rows-min content-start gap-2 overflow-y-auto p-3',
                  stackClasses,
                )
              : clsx('gap-3 p-4', GRID_CLASSES[mode]),
          )}
        >
          {tiles.map((tile) => (
            <SortableTile
              key={tile.key}
              tile={tile}
              canReorder={!mobile && tiles.length > 1}
              fitted={!mobile && mode === 1}
              stack={mobile}
              editMode={editMode}
              onMoveTile={onMoveTile}
              render={(drag) =>
                tile.kind === 'member'
                  ? renderMemberTile(tile.entry, drag)
                  : tile.kind === 'device'
                    ? renderDeviceTile(tile.entry, drag)
                    : renderExternalTile(tile.stream, drag)
              }
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
